import { useEffect, useState, useCallback, useRef } from "react";

import {
  getCodeSubClient,
  type CodeSubHintResponse,
  CodeSubClient,
  TestCase,
  TestStatus,
} from "sj-ui-toolkit/src/client";

// Library imports
import { ProcessedSubmission } from "./subTypes";
import {
  processSubmissionData,
  processPollResult,
  mergeAiHintsWithSubmissions,
} from "./processing";

// Define the return type of the hook
export interface UseSubmissionsResult {
  error: Error | null;
  submissionsLoaded: boolean;
  submissions: ProcessedSubmission[];
  newSubmission: ProcessedSubmission | null;
  provideAiHints: (hints: CodeSubHintResponse[]) => void;
  areHintsProvided: boolean;
  addPollResponse: (pollResponse: TestStatus) => void;
}

// Hook to manage code submissions for a lesson.
export const useSubmissionsInternal = (
  tenant: string,
  lessonKey: string,
  testCases: TestCase[],
  tournamentUuid?: string,
  since?: string
) => {
  // Clients
  const submissionsClient: CodeSubClient = getCodeSubClient();

  // State
  const contextMountedTimeRef = useRef<string>(new Date().toISOString());
  const isInitializedRef = useRef(false);
  const hintsProvidedRef = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [submissions, setSubmissions] = useState<ProcessedSubmission[]>([]);
  const [newSubmission, setNewSubmission] = useState<ProcessedSubmission | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Callback to load submissions from the submissions service
  const loadSubmissions = useCallback(async () => {
    const params: { lessonId: string; since?: string } = {
      lessonId: lessonKey,
    };

    const sinceDate = since ? new Date(since) : null;
    if (sinceDate && sinceDate >= new Date("0001-01-01T00:00:00Z")) {
      params.since = since;
    } else if (tournamentUuid) {
      params.since = contextMountedTimeRef.current;
    }

    const response = await submissionsClient.retrieveCodeSubmission(params);

    if (response.error) {
      console.error("Error loading submissions:", response.error);
      setError(response.error);
      return [];
    }

    return response.data?.userCodeSubmissions ?? [];
  }, [lessonKey]);

  // Callback to add/update a submission. Used when running tests.
  const addPollResponse = useCallback(
    (pollResponse: TestStatus) => {
      // Convert sandbox submission to code submission if needed
      const newSubmission = processPollResult(pollResponse, testCases);

      // Update state using functional update to get current submissions
      setSubmissions(prevSubs => {
        // See if we already have this submission in our list
        const existingSubIndex = newSubmission.runId
          ? prevSubs.findIndex(sub => sub.runId === newSubmission.runId)
          : -1;

        // If we already have this submission, just update it in place
        if (existingSubIndex !== -1) {
          const updated = [...prevSubs];
          updated[existingSubIndex] = newSubmission;
          return updated;
        } else {
          // Otherwise, add it to front of the submissions list
          return [newSubmission, ...prevSubs];
        }
      });

      // Also set as the newSubmission to be displayed
      setNewSubmission(newSubmission);
    },
    [testCases]
  );

  // Callback to merge AI hints with submissions
  const provideAiHints = useCallback(
    (hints: CodeSubHintResponse[]) => {
      hintsProvidedRef.current = true;
      setSubmissions(prevSubmissions => {
        if (prevSubmissions.length === 0) {
          return prevSubmissions;
        }
        return mergeAiHintsWithSubmissions(prevSubmissions, hints);
      });
    },
    [setSubmissions]
  );

  // Load submissions on init or when tenant/lessonKey changes
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const loadedSubmissions = await loadSubmissions();
      if (loadedSubmissions.length > 0) {
        const processedSubmissions = processSubmissionData(loadedSubmissions, testCases) ?? [];
        setSubmissions(processedSubmissions);
      }
      setIsLoading(false);
    };

    if (!isInitializedRef.current) {
      initialize();
      isInitializedRef.current = true;
    }
  }, [tenant, lessonKey, testCases, loadSubmissions]);

  return {
    error,
    submissionsLoaded: isInitializedRef.current && !isLoading,
    submissions,
    newSubmission,
    provideAiHints,
    areHintsProvided: hintsProvidedRef.current,
    addPollResponse,
  };
};
